import { createColumnHelper } from '@tanstack/react-table';
import { pipe } from 'fp-ts/function';
import { array, ord, string } from 'fp-ts';
import { LocalizedEnum, LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { ColumnFilterType, Link, MultiSelectOption, Position } from '@optimus-web/core';
import { EnterpriseBuilderQueueItemsOverviewGetResult, QueueItemState } from '@mews-commander/data-access';
import { LocalizedDateTimeFromUtc, TooltippedTag } from '@mews-commander/ui';
import { tagVariants, tooltipTranslations } from './utils';

const columnHelper = createColumnHelper<EnterpriseBuilderQueueItemsOverviewGetResult>();

const byName = pipe(
    string.Ord,
    ord.contramap((p: MultiSelectOption) => p.value)
);

const createOptions = () =>
    pipe(
        Object.values(QueueItemState).map(
            (value): MultiSelectOption => ({
                value,
                label: <LocalizedEnum prefix="QueueItemState" value={value} />,
            })
        ),
        array.sortBy([byName])
    );

export const getEnterpriseCopyTableColumns = (onEnterpriseClick: (id: string, state: QueueItemState) => void) => [
    columnHelper.accessor('enterpriseName', {
        header: () => <LocalizedText textKey={TextKey.PropertyName} />,
        cell: (props) => (
            <Link onClick={() => onEnterpriseClick(props.row.original.id, props.row.original.state)}>
                {props.getValue()}
            </Link>
        ),
    }),
    columnHelper.accessor('creatorName', {
        header: () => <LocalizedText textKey={TextKey.CreatorName} />,
        cell: (props) => props.getValue(),
    }),
    columnHelper.accessor('createdUtc', {
        header: () => <LocalizedText textKey={TextKey.Created} />,
        cell: (props) => <LocalizedDateTimeFromUtc utcString={props.getValue()} />,
    }),
    columnHelper.accessor('processedUtc', {
        header: () => <LocalizedText textKey={TextKey.Processed} />,
        cell: (props) => (props.getValue() ? <LocalizedDateTimeFromUtc utcString={props.getValue() || ''} /> : null),
    }),
    columnHelper.accessor('state', {
        header: () => <LocalizedText textKey={TextKey.Status} />,
        cell: (props) => (
            <TooltippedTag
                outline
                variant={tagVariants[props.getValue()]}
                tooltip={<LocalizedText textKey={tooltipTranslations[props.getValue()]} />}
                tooltipPosition={Position.Left}
            >
                <LocalizedEnum
                    prefix="QueueItemState"
                    value={
                        props.getValue() === QueueItemState.Waiting || props.getValue() === QueueItemState.Scheduled
                            ? QueueItemState.Processing
                            : props.getValue()
                    }
                />
            </TooltippedTag>
        ),
        enableColumnFilter: true,
        meta: {
            filtersType: ColumnFilterType.MultiSelect,
            getMultiSelectOptions: createOptions,
        },
    }),
];
